import React from "react";
import { motion } from "framer-motion";
import { Moon, Cloud, Palette, Sparkles } from "lucide-react";
import { TeddyBear } from "./icons/TeddyBear";
import { useGender } from "@/contexts/GenderContext";

interface IllustrationProgress {
  current: number;
  total: number;
  description?: string;
  percentage: number;
  stage: 'story' | 'planning' | 'illustrations' | 'finishing' | 'saving' | 'complete';
  message?: string;
}

interface StoryLoadingAnimationProps {
  stage?: "thinking" | "writing" | "polishing";
  illustrationProgress?: IllustrationProgress | null;
}

export function StoryLoadingAnimation({
  stage = "thinking",
  illustrationProgress = null,
}: StoryLoadingAnimationProps) {
  const { selectedGender } = useGender();

  const getLogoSrc = () => {
    switch (selectedGender) {
      case "girl":
        return "/logo-pink.png";
      case "boy":
        return "/logo-blue.png";
      case "other":
        return "/logo-purple.png";
      default:
        return "/logo.png";
    }
  };

  // Dynamic progress messages based on illustration progress
  const getProgressMessage = () => {
    // Debug log to see if progress data is being received
    if (illustrationProgress) {
      console.log('StoryLoadingAnimation: Received progress data:', illustrationProgress);
    }
    
    if (illustrationProgress) {
      // Use real-time progress data
      if (illustrationProgress.message) {
        return illustrationProgress.message;
      }
      
      switch (illustrationProgress.stage) {
        case 'story':
          return "Creating your magical story...";
        case 'planning':
          return "Planning beautiful illustrations...";
        case 'illustrations':
          if (illustrationProgress.current === 0) {
            return `Preparing to create ${illustrationProgress.total} magical illustrations...`;
          }
          // Combine illustration count with description for the main message
          const baseMessage = `Generating illustration ${illustrationProgress.current} of ${illustrationProgress.total}`;
          if (illustrationProgress.description && !illustrationProgress.description.includes('Creating magical')) {
            return `${baseMessage}: ${illustrationProgress.description}`;
          }
          return `${baseMessage}...`;
        case 'finishing':
          return "Adding final touches to your story...";
        case 'saving':
          return "Saving your magical story...";
        case 'complete':
          return "Your magical story is ready!";
        default:
          return "Creating something magical for you...";
      }
    }
    
    // Fallback to static messages
    const stageMessages = {
      thinking: "Gathering magical ingredients...",
      writing: "Weaving your story...",
      polishing: "Adding sparkles and dreams...",
    };
    return stageMessages[stage];
  };

  const stageIcons = {
    thinking: () => (
      <img
        src={getLogoSrc()}
        alt="SnuggleCrafters Logo"
        className="w-24 h-24 object-contain"
        data-testid="loading-logo"
      />
    ),
    writing: () => <Moon className="w-24 h-24 text-primary" />,
    polishing: () => (
      <img
        src={getLogoSrc()}
        alt="SnuggleCrafters Logo"
        className="w-24 h-24 object-contain"
        data-testid="loading-logo"
      />
    ),
  };

  const IconComponent = stageIcons[stage];

  return (
    <div className="flex flex-col items-center justify-center p-8 space-y-6">
      {/* Main animated icon */}
      <motion.div
        className="relative"
        animate={{
          scale: [1, 1.1, 1],
          rotate: [0, 10, -10, 0],
        }}
        transition={{
          duration: 3,
          ease: "easeInOut",
          repeat: Infinity,
        }}
      >
        <IconComponent />

        {/* Floating sparkles around the icon */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            initial={{
              opacity: 0,
              scale: 0,
              x: 0,
              y: 0,
            }}
            animate={{
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
              x: [0, (i % 2 === 0 ? 1 : -1) * (30 + i * 10)],
              y: [0, -30 - i * 5, -60 - i * 10],
            }}
            transition={{
              duration: 2,
              delay: i * 0.3,
              repeat: Infinity,
              ease: "easeOut",
            }}
            style={{
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <img
              src={getLogoSrc()}
              alt="SnuggleCrafters Logo"
              className="w-4 h-4 object-contain opacity-70"
            />
          </motion.div>
        ))}
      </motion.div>

      {/* Progress message */}
      <motion.h3
        className="text-xl font-medium text-primary"
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2, repeat: Infinity }}
        data-testid="progress-message"
      >
        {getProgressMessage()}
      </motion.h3>

      {/* Illustration progress bar - only show when generating illustrations */}
      {illustrationProgress && illustrationProgress.stage === 'illustrations' && (
        <motion.div 
          className="w-full max-w-md space-y-2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Progress bar */}
          <div className="relative w-full h-3 bg-secondary rounded-full overflow-hidden">
            <motion.div
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-primary to-primary/80 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${illustrationProgress.percentage}%` }}
              transition={{ duration: 0.5 }}
            />
            {/* Sparkle effect on progress bar */}
            <motion.div
              className="absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-white/30 to-transparent"
              animate={{ x: [-32, 0] }}
              transition={{ 
                duration: 1.5, 
                repeat: Infinity, 
                repeatDelay: 0.5 
              }}
              style={{ right: `${100 - illustrationProgress.percentage}%` }}
            />
          </div>
          
          {/* Progress text and percentage */}
          <div className="flex justify-between items-center text-sm text-muted-foreground">
            <span data-testid="progress-count">
              {illustrationProgress.current} of {illustrationProgress.total} illustrations
            </span>
            <span className="flex items-center gap-1" data-testid="progress-percentage">
              <Palette className="w-4 h-4" />
              {illustrationProgress.percentage}%
            </span>
          </div>

          {/* Current illustration description */}
          {illustrationProgress.description && (
            <motion.p 
              className="text-sm text-center text-muted-foreground italic max-w-sm mx-auto"
              key={illustrationProgress.description} // Key to trigger re-animation
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.3 }}
              data-testid="illustration-description"
            >
              {illustrationProgress.description}
            </motion.p>
          )}
        </motion.div>
      )}

      {/* Teddy jumping over moon animation */}
      <div className="relative w-full h-32 overflow-hidden">
        {/* Moon */}
        <motion.div
          className="absolute left-1/2 top-8"
          initial={{ scale: 0.8, opacity: 0.5 }}
          animate={{
            scale: [0.8, 1, 0.8],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          style={{
            transform: "translateX(-50%)",
          }}
        >
          <Moon className="w-16 h-16 text-yellow-200 fill-yellow-100" />
        </motion.div>

        {/* Teddy Bear jumping animation */}
        <motion.div
          className="absolute"
          initial={{
            x: -50,
            y: 60,
            rotate: -15,
          }}
          animate={{
            x: [-50, 100, 250, 400],
            y: [60, 0, 0, 60],
            rotate: [-15, -360, -720, -735],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
            times: [0, 0.3, 0.7, 1],
            repeatDelay: 1,
          }}
        >
          {/* Teddy SVG */}
          <img
            src="/teddy.svg"
            alt="Teddy Bear"
            width="40"
            height="40"
            className="text-amber-600"
          />
        </motion.div>

        {/* Stars scattered around */}
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 1, 0],
              scale: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 2,
              delay: i * 0.4,
              repeat: Infinity,
            }}
            style={{
              left: `${20 + i * 15}%`,
              top: `${10 + (i % 2) * 30}px`,
            }}
          >
            <TeddyBear className="w-3 h-3 text-yellow-300 fill-yellow-200" />
          </motion.div>
        ))}
      </div>

      {/* Progress dots */}
      <div className="flex space-x-2">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-3 h-3 bg-primary rounded-full"
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.5,
              delay: i * 0.2,
              repeat: Infinity,
            }}
          />
        ))}
      </div>

      {/* Encouraging message - dynamic based on illustration progress */}
      <motion.p
        className="text-sm text-muted-foreground text-center max-w-md"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        data-testid="encouragement-message"
      >
        {illustrationProgress && illustrationProgress.stage === 'illustrations' 
          ? `Creating beautiful illustrations for your story! Each one is carefully crafted to bring your tale to life. This may take a few moments...`
          : illustrationProgress && illustrationProgress.stage === 'story'
          ? `Your personalized bedtime story is being written just for you with love and imagination...`
          : illustrationProgress && illustrationProgress.stage === 'planning'
          ? `Planning the perfect illustrations to accompany your magical story...`
          : `Your personalized bedtime story is being crafted with love and imagination. This usually takes about 10-15 seconds.`}
      </motion.p>
    </div>
  );
}
